import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'react-toastify';
import { FUNDING_SOURCE } from '@paypal/paypal-js';
import { useMutation } from '@apollo/client';
import {
  AUTHORIZE_PAYMENT_TOKEN,
  CREATE_PAYMENT_TOKEN,
} from '@monofront/business/queries';
import {
  IAuthorizePaymentTokenResult,
  ICreatePaymentTokenResult,
  PaymentMethodType,
  PaymentTokenType,
  PaymentMethodSource,
} from '@monofront/types';
import logger from '@utils/logger';
import PaypalButtonContainer from '@components/PaypalButtonContainer';
import { PayPalButtons } from '@paypal/react-paypal-js';

enum PaypalErrors {
  onApprove,
}

interface IPaypalButtonProps {
  autoInit?: boolean;
  fundingSource?: FUNDING_SOURCE;
  onStartBillingAgreement?: () => void;
  onCancel?: () => void;
  onError?: (error: any) => void;
  onApprove: (
    paymentMethodId: string,
    paymentMethodeType: PaymentMethodType,
    paymentMethodSource: PaymentMethodSource,
  ) => void;
}

const PaypalAgreementButton: React.FC<IPaypalButtonProps> = ({
  fundingSource = 'paypal',
  onStartBillingAgreement,
  onCancel,
  onError,
  onApprove,
}) => {
  const [hideButton, setHideButton] = useState(true); // hide paypal button while not ready for interaction
  const [createPaymentToken] =
    useMutation<ICreatePaymentTokenResult>(CREATE_PAYMENT_TOKEN);
  const [authorizePaymentToken] = useMutation<IAuthorizePaymentTokenResult>(
    AUTHORIZE_PAYMENT_TOKEN,
  );
  const tErrors = useTranslations('Errors');

  return (
    <PaypalButtonContainer hideButton={hideButton}>
      {PayPalButtons && (
        <PayPalButtons
          key={fundingSource} // force remount component when the founding source changes (paypal to paylater, on checkbox selection), as the PaypalButton component doesn't remount by itself
          fundingSource={fundingSource}
          style={{
            layout: 'horizontal',
            tagline: false,
            shape: 'pill',
            color: 'gold',
            height: 45,
          }}
          onInit={() => {
            setHideButton(false);
          }}
          createBillingAgreement={async () => {
            // createBillingAgreement is triggered on click on the Paypal Agreement button
            onStartBillingAgreement?.();

            const { data, errors } = await createPaymentToken({
              variables: {
                paymentTokenType: PaymentTokenType.PAYPAL_BILLING_AGREEMENT,
              },
            });

            if (errors || !data) {
              throw new Error('Failed to create payment token');
            }

            return data.createPaymentToken.authorizationToken;
          }}
          onApprove={async ({ billingToken }) => {
            setHideButton(true);
            if (billingToken) {
              const { data, errors } = await authorizePaymentToken({
                variables: {
                  token: billingToken,
                  paymentTokenType: PaymentTokenType.PAYPAL_BILLING_AGREEMENT,
                },
              });

              if (errors || !data) {
                throw new Error('Failed to authorize payment token');
              }

              onApprove(
                data?.authorizePaymentToken.token,
                PaymentMethodType.PAYPAL,
                PaymentMethodSource.PAYPAL,
              );
            }
          }}
          onCancel={() => {
            if (typeof onCancel === 'function') {
              onCancel();
            }
          }}
          onError={(error) => {
            setHideButton(false);
            if (typeof onError === 'function') {
              onError(error as any);

              return;
            }

            logger.error(
              '[payment] Error occurred during payment subscription',
              {
                error,
              },
            );

            if ((error as unknown) === String(PaypalErrors.onApprove)) {
              toast.error(tErrors('paymentGenericError'));
            } else {
              toast.error(tErrors('unexpectedPaymentError'));
            }
          }}
        />
      )}
    </PaypalButtonContainer>
  );
};

export default PaypalAgreementButton;
